import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  User,
  Package,
  MessageCircle,
  ShoppingCart,
  Edit3,
  Save,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/Urls";
import { CLOUDINARY_PURPOSES, uploadDirectToCloudinary } from "../lib/cloudinaryUpload";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import ProfileImageViewer from "../components/ProfileImageViewer";
import { getStoredUser, getStoredUserId, saveStoredUser } from "../lib/authStorage";


const CHAT_COUNT_CACHE_TTL_MS = 30 * 1000;

const getChatCountCacheKey = (userId) => `chatCount:${userId}`;

const readCachedChatCount = (userId) => {
  if (!userId) return null;

  try {
    const raw = sessionStorage.getItem(getChatCountCacheKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const count = Number(parsed?.count);
    const updatedAt = Number(parsed?.updatedAt);

    if (!Number.isFinite(count) || !Number.isFinite(updatedAt)) {
      return null;
    }

    return { count, updatedAt };
  } catch {
    return null;
  }
};

const writeCachedChatCount = (userId, count) => {
  if (!userId) return;

  sessionStorage.setItem(
    getChatCountCacheKey(userId),
    JSON.stringify({ count, updatedAt: Date.now() })
  );
};


const ProfilePage = () => {
  const navigate = useNavigate();
  const { count: cartCount } = useCart();
  const [relistingId, setRelistingId] = useState(null);

  const [actionLoading, setActionLoading] = useState({
    itemId: null,
    action: null,
  });

  const isActionLoading = (itemId, action) =>
  actionLoading.itemId === itemId &&
  actionLoading.action === action;

  const userId = useMemo(
    () => getStoredUserId(),
    []
  );

  // ===================== INSTANT HYDRATION =====================
  const [user, setUser] = useState(() => getStoredUser());

  const [items, setItems] = useState(() => {
    try {
      const cached = sessionStorage.getItem("category_items_all");
      const parsed = cached ? JSON.parse(cached) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      sessionStorage.removeItem("category_items_all");
      return [];
    }
  });

  const [editMode, setEditMode] = useState(false);
  const [contactEditMode, setContactEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  const [profileImage, setProfileImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const imageUrlRef = useRef(null);
  const fetchCache = useRef({ user: null, items: null });

  const [chatCount, setChatCount] = useState(() => {
    const cached = readCachedChatCount(userId);
    return cached?.count ?? 0;
  });

  const [form, setForm] = useState(() => {
    const u = getStoredUser();

    return {
      username: u?.username || "",
      email: u?.email || "",
      phone: u?.phone || "",
      location: u?.location || "",
      whatsapp: u?.whatsapp || "",
      country: u?.country || "",
      city: u?.city || "",
    };
  });

  // ===================== FETCH DATA =====================
  useEffect(() => {
    if (!userId) return;

    const fetchUser = async () => {
      try {
        if (!fetchCache.current.user) {
          fetchCache.current.user = api.get(`/api/users/${userId}`);
        }

        const res = await fetchCache.current.user;

        setUser(res.data);
        setForm({
          username: res.data.username || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          location: res.data.location || "",
          whatsapp: res.data.whatsapp || "",
          country: res.data.country || "",
          city: res.data.city || "",
        });

        saveStoredUser(res.data);
      } catch (err) {
        console.error("Failed to fetch user", err);
      }
    };


    const fetchChats = async ({ force = false } = {}) => {
      const cached = readCachedChatCount(userId);

      if (cached) {
        setChatCount(cached.count);

        const isFresh =
          Date.now() - cached.updatedAt < CHAT_COUNT_CACHE_TTL_MS;

        if (isFresh && !force) {
          return cached.count;
        }
      }

      try {
        // Profile only needs the number of conversations.
        const res = await api.get("/api/chat/inbox/count");
        const count = Number(res.data?.count || 0);
        const safeCount = Number.isFinite(count) ? count : 0;

        setChatCount(safeCount);
        writeCachedChatCount(userId, safeCount);

        return safeCount;
      } catch (err) {
        console.error("Failed to fetch chat count", err);

        // Keep the last known cached value instead of flashing back to 0.
        return cached?.count ?? 0;
      }
    };

    fetchChats();

    const handleFocus = () => {
      fetchChats();
    };

    // Re-check after returning to the app, but respect the short TTL.
    window.addEventListener("focus", handleFocus);


    const fetchItems = async () => {
      try {
        if (!fetchCache.current.items) {
          fetchCache.current.items = api.get("/api/category-items");
        }

        const res = await fetchCache.current.items;

        setItems(res.data);
        sessionStorage.setItem("category_items_all", JSON.stringify(res.data));
      } catch (err) {
        console.error(err);
      }
    };

    fetchUser();
    fetchItems();

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [userId]);

  // ===================== DERIVED DATA =====================
  const myItems = useMemo(() => {
    return items.filter((i) => i.userId === userId);
  }, [items, userId]);

  const fallback = useCallback((v) => v || "Not set", []);

  // ===================== HANDLERS =====================
  const handleChange = useCallback((e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProfileImage(file);

    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
    }

    const url = URL.createObjectURL(file);
    imageUrlRef.current = url;
    setPreview(url);
  }, []);

  const uploadImage = useCallback(async () => {
    if (!profileImage) return;

    const uploaded = await uploadDirectToCloudinary(
      profileImage,
      CLOUDINARY_PURPOSES.PROFILE_IMAGE
    );

    const res = await api.put(`/api/users/${userId}/profile-image`, {
      imageUrl: uploaded.secureUrl,
    });

    setUser(res.data);
    saveStoredUser(res.data);
  }, [profileImage, userId]);

  const saveProfile = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.put(`/api/users/${userId}`, form);

      const updated = res.data;

      setUser(updated);

      saveStoredUser(updated);

      if (profileImage) {
        await uploadImage();
      }

      setEditMode(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [form, profileImage, uploadImage, userId]);

  const cancelContactEdit = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      phone: user?.phone || "",
      whatsapp: user?.whatsapp || "",
      country: user?.country || "",
      city: user?.city || "",
      location: user?.location || "",
    }));

    setContactEditMode(false);
  }, [user]);

  const saveContactDetails = useCallback(async () => {
    try {
      setContactLoading(true);

      // Keep the rest of the profile unchanged and update contact fields only.
      const payload = {
        ...(user || {}),
        phone: form.phone,
        whatsapp: form.whatsapp,
        country: form.country,
        city: form.city,
        location: form.location,
      };

      const res = await api.put(`/api/users/${userId}`, payload);
      const updated = res.data;

      setUser(updated);
      setForm((prev) => ({
        ...prev,
        phone: updated.phone || "",
        whatsapp: updated.whatsapp || "",
        country: updated.country || "",
        city: updated.city || "",
        location: updated.location || "",
      }));

      saveStoredUser(updated);
      setContactEditMode(false);
    } catch (err) {
      console.error("Failed to update contact details", err);
    } finally {
      setContactLoading(false);
    }
  }, [
    form.phone,
    form.whatsapp,
    form.country,
    form.city,
    form.location,
    user,
    userId,
  ]);

  // cleanup
  useEffect(() => {
    return () => {
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
      }
    };
  }, []);

  const markAsSold = async (itemId) => {
    try {
      setActionLoading({ itemId, action: "sold" });

      const res = await api.put(`/api/category-items/${itemId}/status`, null, {
        params: { status: "sold" },
      });

      setItems((prev) =>
        prev.map((item) => (item.itemId === itemId ? res.data : item))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading({ itemId: null, action: null });
    }
  };


  const getItemStatus = (item) => {
    if (item.status === "sold") {
      return "Sold";
    }

    const posted = new Date(item.timestamp).getTime();

    const expiry =
      posted + Number(item.availPeriod) * 60 * 60 * 1000;

    if (Date.now() >= expiry) {
      return "Expired";
    }

    return timeAgo(item.timestamp);
  };


  const isExpired = (item) => {
    const posted = new Date(item.timestamp).getTime();

    const expiry =
      posted + Number(item.availPeriod) * 60 * 60 * 1000;

    return Date.now() >= expiry;

  };


  const resaleItem = async (itemId) => {
    try {
      setActionLoading({ itemId, action: "relist" });

      await api.put(`/api/category-items/${itemId}/resale`);

      setItems((prev) =>
        prev.map((item) =>
          item.itemId === itemId
            ? {
                ...item,
                status: "available",
                timestamp: new Date().toISOString(),
              }
            : item
        )
      );
      alert("Item relisted successfully");
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading({ itemId: null, action: null });
    }
  };


  const deleteItem = async (itemId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this item?");
    if (!confirmDelete) return;

    try {
      setActionLoading({ itemId, action: "delete" });

      await api.delete(`/api/category-items/${itemId}`);

      setItems((prev) => prev.filter((item) => item.itemId !== itemId));
      alert("Item deleted successfully");
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading({ itemId: null, action: null });
    }
  };

  return (
  <div className="min-h-screen bg-background flex flex-col">
    <Navbar />

    <div className="container mx-auto px-4 py-8 flex-1">
      {/* ===================================================== */}
      {/* PROFILE HEADER */}
      {/* ===================================================== */}

      <div className="mb-6 bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* PROFILE IMAGE */}
          <div className="relative h-16 w-16 shrink-0">
            <ProfileImageViewer
              src={
                preview
                  ? preview
                  : user?.profileImageUrl
                  ? user.profileImageUrl
                  : "https://ui-avatars.com/api/?name=" +
                    form.username +
                    "&background=random&color=fff&size=128"
              }
              alt={`${form.username || "User"} profile`}
              title={form.username || "Profile image"}
              triggerClassName="h-16 w-16 rounded-full"
              imageClassName="h-16 w-16 rounded-full object-cover border"
            />

            {editMode && (
              <label className="absolute bottom-0 right-0 bg-black text-white h-6 w-6 flex items-center justify-center rounded-full cursor-pointer text-xs border border-background">
                ✎

                <input
                  type="file"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            )}
          </div>

          {/* ================================================= */}
          {/* USER NAME / EMAIL */}
          {/* ================================================= */}

          <div className="min-w-0 flex-1">
            {editMode ? (
              <input
                name="username"
                value={form.username}
                onChange={handleChange}
                className="
                  w-full
                  min-w-0
                  max-w-[180px]
                  sm:max-w-[260px]
                  text-lg
                  sm:text-xl
                  font-bold
                  bg-transparent
                  border-b
                  border-primary
                  outline-none
                  pb-1
                "
              />
            ) : (
              <h2 className="truncate text-lg sm:text-xl font-bold">
                {fallback(form.username)}
              </h2>
            )}

            <p className="truncate text-xs sm:text-sm text-muted-foreground mt-1">
              {fallback(form.email)}
            </p>
          </div>

          {/* ================================================= */}
          {/* DESKTOP EDIT BUTTONS */}
          {/* ================================================= */}

          <div className="hidden sm:flex ml-auto items-center gap-2 shrink-0">
            {editMode ? (
              <>
                <button
                  onClick={() => setEditMode(false)}
                  className="
                    h-9
                    px-3
                    text-sm
                    border
                    border-border
                    rounded-lg
                    flex
                    items-center
                    gap-1
                    bg-background
                    hover:bg-muted
                    transition
                  "
                >
                  <X size={15} />
                  Cancel
                </button>

                <button
                  onClick={saveProfile}
                  disabled={loading}
                  className="
                    h-9
                    px-3
                    text-sm
                    bg-primary
                    text-white
                    rounded-lg
                    flex
                    items-center
                    gap-1
                    hover:opacity-90
                    transition
                    disabled:opacity-60
                  "
                >
                  {loading ? (
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={15} />
                  )}

                  {loading ? "Saving..." : "Save"}
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditMode(true)}
                disabled={contactEditMode}
                className="
                  h-9
                  px-3
                  text-sm
                  border
                  border-border
                  rounded-lg
                  flex
                  items-center
                  gap-1
                  bg-background
                  hover:bg-muted
                  transition
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                "
              >
                <Edit3 size={15} />
                Edit Profile
              </button>
            )}
          </div>

          {/* ================================================= */}
          {/* MOBILE EDIT BUTTONS */}
          {/* ================================================= */}

          <div className="flex sm:hidden items-center gap-1.5 shrink-0">
            {editMode ? (
              <>
                {/* CANCEL */}
                <button
                  onClick={() => setEditMode(false)}
                  aria-label="Cancel editing"
                  className="
                    h-9
                    w-9
                    shrink-0
                    border
                    border-border
                    rounded-full
                    flex
                    items-center
                    justify-center
                    bg-background
                    hover:bg-muted
                    transition
                  "
                >
                  <X size={15} />
                </button>

                {/* SAVE */}
                <button
                  onClick={saveProfile}
                  disabled={loading}
                  aria-label="Save profile"
                  className="
                    h-9
                    w-9
                    shrink-0
                    bg-primary
                    text-white
                    rounded-full
                    flex
                    items-center
                    justify-center
                    transition
                    disabled:opacity-60
                  "
                >
                  {loading ? (
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={15} />
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditMode(true)}
                disabled={contactEditMode}
                aria-label="Edit profile"
                className="
                  h-9
                  w-9
                  shrink-0
                  border
                  border-border
                  rounded-full
                  flex
                  items-center
                  justify-center
                  bg-background
                  hover:bg-muted
                  transition
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                "
              >
                <Edit3 size={15} />
              </button>
            )}
          </div>
        </div>

        {/* ===================================================== */}
        {/* STATS */}
        {/* ===================================================== */}

        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6">
          {/* LISTINGS */}
          <div
            className="
              bg-secondary
              rounded-lg
              p-2.5
              sm:p-3
              text-center
              cursor-pointer
              transition-colors
              hover:bg-muted
              min-w-0
            "
            onClick={() =>
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
              })
            }
          >
            <Package className="h-5 w-5 mx-auto mb-1 text-primary" />

            <p className="text-sm font-semibold">
              {myItems.length}
            </p>

            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
              Listings
            </p>
          </div>

          {/* CHATS */}
          <Link
            to="/inbox"
            className="min-w-0"
          >
            <div
              className="
                h-full
                bg-secondary
                rounded-lg
                p-2.5
                sm:p-3
                text-center
                transition-colors
                hover:bg-muted
              "
            >
              <MessageCircle className="h-5 w-5 mx-auto mb-1 text-primary" />

              <p className="text-sm font-semibold">
                {chatCount}
              </p>

              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                Chats
              </p>
            </div>
          </Link>

          {/* CART */}
          <Link
            to="/cart"
            className="min-w-0"
          >
            <div
              className="
                h-full
                bg-secondary
                rounded-lg
                p-2.5
                sm:p-3
                text-center
                transition-colors
                hover:bg-muted
              "
            >
              <ShoppingCart className="h-5 w-5 mx-auto mb-1 text-primary" />

              <p className="text-sm font-semibold">
                {cartCount}
              </p>

              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                Cart
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* ===================================================== */}
      {/* CONTACT INFORMATION */}
      {/* ===================================================== */}

      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-sm space-y-2.5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-semibold">
            Contact Information
          </h3>

          {contactEditMode ? (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={cancelContactEdit}
                disabled={contactLoading}
                aria-label="Cancel contact editing"
                className="
                  h-9
                  px-3
                  text-sm
                  border
                  border-border
                  rounded-lg
                  flex
                  items-center
                  gap-1
                  bg-background
                  hover:bg-muted
                  transition
                  disabled:opacity-50
                "
              >
                <X size={15} />
                <span className="hidden sm:inline">Cancel</span>
              </button>

              <button
                onClick={saveContactDetails}
                disabled={contactLoading}
                aria-label="Save contact details"
                className="
                  h-9
                  px-3
                  text-sm
                  bg-primary
                  text-white
                  rounded-lg
                  flex
                  items-center
                  gap-1
                  hover:opacity-90
                  transition
                  disabled:opacity-60
                "
              >
                {contactLoading ? (
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save size={15} />
                )}

                <span className="hidden sm:inline">
                  {contactLoading ? "Saving..." : "Save"}
                </span>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setContactEditMode(true)}
              disabled={editMode}
              aria-label="Edit contact details"
              className="
                h-9
                px-3
                text-sm
                border
                border-border
                rounded-lg
                flex
                items-center
                gap-1
                bg-background
                hover:bg-muted
                transition
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              <Edit3 size={15} />
              <span className="hidden sm:inline">Edit Contacts</span>
            </button>
          )}
        </div>

        {["phone", "whatsapp", "country", "city", "location"].map(
          (field) => (
            <div
              key={field}
              className="
                flex
                flex-col
                gap-0
                sm:flex-row
                sm:items-center
                sm:justify-between
                text-sm
                min-w-0
              "
            >
              <span className="text-muted-foreground capitalize shrink-0">
                {field}
              </span>

              {contactEditMode ? (
                <input
                  name={field}
                  value={form[field] || ""}
                  onChange={handleChange}
                  className="
                    w-full
                    min-w-0
                    sm:w-auto
                    sm:max-w-[65%]
                    border-b
                    border-primary
                    bg-transparent
                    outline-none
                    text-left
                    sm:text-right
                    text-primary
                    pb-0
                  "
                />
              ) : (
                <span
                  className="
                    min-w-0
                    break-words
                    sm:text-right
                  "
                >
                  {fallback(form[field])}
                </span>
              )}
            </div>
          )
        )}
      </div>

      {/* ===================================================== */}
      {/* MY LISTINGS */}
      {/* ===================================================== */}

      <div className="mt-8">
        <h3 className="text-lg font-bold mb-2">
          My Listings
        </h3>

        <p className="text-sm text-muted-foreground mb-6">
          Manage your posted items. Click on an item to view details or edit.
        </p>

        {myItems.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <Package className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />

            <p className="text-sm font-medium">
              No items yet
            </p>

            <p className="mt-1 text-xs text-muted-foreground">
              Your posted items will appear here.
            </p>
          </div>
        ) : (
          <div
            className="
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-3
              gap-4
            "
          >
            {myItems.map((item) => (
              <Link
                key={item.itemId}
                to={`/item/${item.itemId}`}
                className="min-w-0"
              >
                <div
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-lg
                    border
                    border-border
                    bg-card
                    transition-all
                    hover:border-primary/40
                    hover:shadow-[var(--glow-primary)]
                  "
                  style={{
                    animationDelay: `${60}ms`,
                  }}
                >
                  {/* =========================================== */}
                  {/* IMAGE */}
                  {/* =========================================== */}

                  <div className="relative h-48 overflow-hidden">
                    {/* EDIT BUTTON */}
                    {item.status !== "sold" && !isExpired(item) && (
                      <div
                        className="
                          absolute
                          top-3
                          left-3
                          z-20
                          rounded-md
                          px-3
                          py-1.5
                          sm:px-4
                          sm:py-2
                          text-[11px]
                          sm:text-xs
                          font-medium
                          backdrop-blur-md
                          border
                          border-border
                          bg-background/80
                          text-primary
                          cursor-pointer
                          hover:bg-primary/10
                          transition
                          shadow-sm
                        "
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          navigate(
                            `/edit-item/${item.itemId}`
                          );
                        }}
                      >
                        Edit
                      </div>
                    )}

                    {/* ========================================= */}
                    {/* BOTTOM STATUS/ACTION BUTTONS */}
                    {/* ========================================= */}

                    <div
                      className="
                        absolute
                        bottom-3
                        left-3
                        right-3
                        z-20
                        flex
                        flex-wrap
                        items-center
                        justify-end
                        gap-1.5
                        sm:gap-2
                      "
                    >
                      {/* STATUS */}
                      <div
                        className={`
                          rounded-md
                          px-2.5
                          py-1.5
                          sm:px-4
                          sm:py-2
                          text-[10px]
                          sm:text-xs
                          font-medium
                          backdrop-blur-md
                          border
                          border-border
                          whitespace-nowrap

                          ${
                            item.status === "sold"
                              ? "bg-red-500/20 text-red-400"
                              : isExpired(item)
                              ? "bg-orange-500/20 text-orange-300"
                              : "bg-black/70 text-yellow-400"
                          }
                        `}
                      >
                        {item.status === "sold"
                          ? "Sold"
                          : isExpired(item)
                          ? "Expired"
                          : "Available"}
                      </div>

                      {/* ======================================= */}
                      {/* SOLD -> DELETE */}
                      {/* ======================================= */}

                      {item.status === "sold" ? (
                        <div
                          className="
                            min-h-8
                            rounded-md
                            px-2.5
                            py-1.5
                            sm:px-4
                            sm:py-2
                            text-[10px]
                            sm:text-xs
                            font-medium
                            backdrop-blur-md
                            border
                            border-border
                            bg-red-500/20
                            text-red-400
                            cursor-pointer
                            hover:bg-red-500/30
                            transition
                            flex
                            items-center
                            justify-center
                            whitespace-nowrap
                          "
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            deleteItem(item.itemId);
                          }}
                        >
                          {isActionLoading(
                            item.itemId,
                            "delete"
                          ) ? (
                            <div className="h-4 w-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <span>Delete</span>
                          )}
                        </div>
                      ) : isExpired(item) ? (
                        /* ===================================== */
                        /* EXPIRED -> RELIST */
                        /* ===================================== */

                        <div
                          className="
                            min-h-8
                            rounded-md
                            px-2.5
                            py-1.5
                            sm:px-4
                            sm:py-2
                            text-[10px]
                            sm:text-xs
                            font-medium
                            backdrop-blur-md
                            border
                            border-border
                            bg-primary/20
                            text-primary
                            cursor-pointer
                            hover:bg-primary/30
                            transition
                            flex
                            items-center
                            justify-center
                            whitespace-nowrap
                          "
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            resaleItem(item.itemId);
                          }}
                        >
                          {isActionLoading(
                            item.itemId,
                            "relist"
                          ) ? (
                            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <span>Relist</span>
                          )}
                        </div>
                      ) : (
                        /* ===================================== */
                        /* AVAILABLE -> MARK SOLD */
                        /* ===================================== */

                        <div
                          className="
                            min-h-8
                            rounded-md
                            px-2.5
                            py-1.5
                            sm:px-4
                            sm:py-2
                            text-[10px]
                            sm:text-xs
                            font-medium
                            backdrop-blur-md
                            border
                            border-border
                            bg-black/70
                            text-yellow-400
                            cursor-pointer
                            hover:bg-black
                            transition
                            flex
                            items-center
                            justify-center
                            whitespace-nowrap
                          "
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            markAsSold(item.itemId);
                          }}
                        >
                          {isActionLoading(
                            item.itemId,
                            "sold"
                          ) ? (
                            <div className="h-4 w-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <span>Mark as Sold</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ITEM IMAGE */}
                    <img
                      src={item.imageUrl}
                      alt={item.itemName}
                      className="
                        h-full
                        w-full
                        object-cover
                        transition-transform
                        duration-500
                        group-hover:scale-110
                      "
                      loading="lazy"
                    />
                  </div>

                  {/* =========================================== */}
                  {/* CARD CONTENT */}
                  {/* =========================================== */}

                  <div className="px-4 sm:px-5 py-4 min-w-0">
                    <h3
                      className="
                        truncate
                        text-primary
                        font-heading
                        text-lg
                        font-semibold
                        transition-colors
                        group-hover:text-primary
                      "
                    >
                      {item.itemName}
                    </h3>

                    <p className="mt-1 text-sm text-muted-foreground truncate">
                      {item.description}
                    </p>

                    <p className="mt-1 text-sm text-muted-foreground font-medium">
                      {item.currency}{" "}
                      {Number(item.amount).toLocaleString()}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>

    <Footer />
  </div>
);
};

export default ProfilePage;
